// apps/admin/src/pages/settings/GeneralTab.tsx
//
// Phase 8.3 Wave 6 Task 41 — General settings tab.
//
// vg-binding: CONTEXT:decisions=P8.D-100,P8.D-101,P8.D-120,P8.D-170
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Route: /system/settings/general
//
// Scope (per task-41 PLAN + API-CONTRACTS § GeneralSettingsValues):
//   - platform_name (string 2-100)
//   - logo_url, favicon_url (URL inputs — MediaPicker is a follow-up;
//     this tab ships a plain URL field per the strict zod shape)
//   - default_locale (en / vi / fr)
//   - default_date_format (free string — UI hints common patterns)
//   - default_timezone (free string — IANA TZ identifier)
//   - session_timeout_minutes (int 5-480)
//   - maintenance.* (active + custom_message + duration_hours + bypass_ips)
//     Maintenance toggle is dual-approval-gated → the toggle here just
//     surfaces a banner reminder; the actual dual-approval flow lives in
//     task 50 (/system/settings/maintenance-mode).
//
// Permission gates:
//   - Read: `system:settings:read`
//   - Write: `system:settings:write:general`

import { useCallback, useMemo, type JSX } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FileUploadInput,
  Form,
  FormControl,
  FormField,
  FormLabel,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Spinner,
  cn,
  useAuth,
  usePermission,
} from '@printway/ui';

import { API_BASE_URL } from '../../lib/apiClient.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { ConflictDiffModal } from '../../features/settings/ConflictDiffModal.js';
import { StickySaveBar } from '../../features/settings/StickySaveBar.js';
import { useSettings } from '../../features/settings/useSettings.js';
import type { GeneralSettingsValues } from '../../api/systemSettings.api.js';

const LOCALE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'en', label: 'English (en)' },
  { value: 'vi', label: 'Tiếng Việt (vi)' },
  { value: 'fr', label: 'Français (fr)' },
];

const COMMON_TIMEZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho_Chi_Minh (UTC+7)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'America/New_York (UTC−5/−4)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC−8/−7)' },
  { value: 'Europe/London', label: 'Europe/London (UTC+0/+1)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
];

const DATE_FORMAT_OPTIONS: ReadonlyArray<{ value: string; labelKey: string }> = [
  { value: 'DD/MM/YYYY', labelKey: 'general.dateFormat.options.vi' },
  { value: 'MM/DD/YYYY', labelKey: 'general.dateFormat.options.us' },
  { value: 'YYYY-MM-DD', labelKey: 'general.dateFormat.options.iso' },
];

// Stable empty-roles reference (dbg-20260710 #2593). A bare `[]` literal
// inside the useAuth selector returns a NEW array every render whenever
// `user.roles` is nullish, so useSyncExternalStore sees a changed snapshot
// each pass → "getSnapshot should be cached" → infinite re-render →
// "Maximum update depth exceeded" (white error screen on /system/settings).
// Select the raw value and coalesce against this frozen constant instead.
const EMPTY_ROLES: readonly unknown[] = [];

// Full-shape default (dbg-20260710 #2593). The BE `general` namespace default
// (DEFAULT_SETTINGS.general) is sparse — flat `maintenance_mode`, no nested
// `maintenance` object, no default_locale/logo_url/session_timeout — while
// this tab reads `form.maintenance.active` etc. Rendering the raw envelope
// therefore crashed with "Cannot read properties of undefined (reading
// 'active')". Merge the envelope over this complete shape so every field the
// JSX reads always exists. (BE↔FE field-name drift, e.g. date_format vs
// default_date_format, is a separate follow-up — defaults show meanwhile.)
const DEFAULT_GENERAL: GeneralSettingsValues = {
  platform_name: '',
  logo_url: null,
  favicon_url: null,
  default_timezone: 'Asia/Ho_Chi_Minh',
  default_date_format: 'YYYY-MM-DD',
  default_locale: 'en',
  session_timeout_minutes: 30,
  maintenance: {
    active: false,
    custom_message: '',
    duration_hours: 0,
    bypass_ips: [],
  },
};

export function GeneralTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const canRead = usePermission('system:settings:read');
  const canWrite = usePermission('system:settings:write:general');
  const userRoles = useAuth((s) => s.user?.roles) ?? EMPTY_ROLES;
  const isSuperAdmin = useMemo(
    () =>
      userRoles.some((r) =>
        typeof r === 'string'
          ? r === 'superadmin'
          : (r as { role?: string })?.role === 'superadmin',
      ),
    [userRoles],
  );

  const {
    loading,
    saving,
    envelope,
    draft,
    dirty,
    conflict,
    setDraft,
    reload,
    save,
    forceOverwrite,
    dismissConflict,
  } = useSettings<GeneralSettingsValues>('general');

  const update = useCallback(
    <K extends keyof GeneralSettingsValues>(
      key: K,
      value: GeneralSettingsValues[K],
    ): void => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    [setDraft],
  );

  const updateMaintenance = useCallback(
    <K extends keyof GeneralSettingsValues['maintenance']>(
      key: K,
      value: GeneralSettingsValues['maintenance'][K],
    ): void => {
      setDraft((prev) => ({
        ...prev,
        maintenance: { ...prev.maintenance, [key]: value },
      }));
    },
    [setDraft],
  );

  if (!canRead) {
    return (
      <div className="rounded-lg border border-pw-border bg-pw-surface p-6 text-sm text-pw-text-muted">
        {t('general.noPermission')}
      </div>
    );
  }

  if (loading || !envelope || !draft) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner label={t('general.loading')} />
      </div>
    );
  }

  // Normalized read-model: envelope values merged over the full default shape
  // so JSX reads (form.maintenance.active, form.default_locale, …) never hit
  // undefined even when the BE envelope is sparse. Mutations still target the
  // raw `draft` via setDraft; the merge keeps rendering resilient (#2593).
  const form: GeneralSettingsValues = {
    ...DEFAULT_GENERAL,
    ...draft,
    maintenance: {
      ...DEFAULT_GENERAL.maintenance,
      ...(draft.maintenance ?? {}),
    },
  };

  return (
    <div className="flex flex-col gap-6" data-testid="settings-general-tab">
      {/* Version + last-writer meta line */}
      <div className="flex items-center justify-between text-xs text-pw-text-muted">
        <span>
          {t('general.versionLabel')}{' '}
          <strong className="font-semibold">v{envelope.version}</strong>
          {envelope.updated_by_name ? (
            <> · {t('general.lastEditedBy', { name: envelope.updated_by_name })}</>
          ) : null}
          {envelope.updated_at ? (
            <> · {new Date(envelope.updated_at).toLocaleString()}</>
          ) : null}
        </span>
        <button
          type="button"
          onClick={() => {
            void reload();
          }}
          className="text-xs font-medium text-pw-primary-strong hover:underline"
          data-testid="settings-general-reload-btn"
        >
          {t('general.reload')}
        </button>
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Card>
          <CardHeader>
            <CardTitle>{t('general.platformIdentity.title')}</CardTitle>
            <CardDescription>
              {t('general.platformIdentity.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormField>
              <FormLabel htmlFor="general-platform-name">{t('general.platformName.label')}</FormLabel>
              <FormControl>
                <Input
                  id="general-platform-name"
                  value={form.platform_name}
                  onChange={(e) => update('platform_name', e.target.value)}
                  minLength={2}
                  maxLength={100}
                  disabled={!canWrite}
                  data-testid="settings-general-platform-name"
                />
              </FormControl>
              <p className="text-xs text-pw-text-muted">{t('general.platformName.hint')}</p>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-logo-url">{t('general.logo.label')}</FormLabel>
              <FormControl>
                <FileUploadInput
                  apiBaseUrl={API_BASE_URL}
                  value={form.logo_url ?? ''}
                  onChange={(url) => update('logo_url', url === '' ? null : url)}
                  disabled={!canWrite}
                  testId="settings-general-logo-url"
                  helperText={t('general.logo.helper')}
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-favicon-url">{t('general.favicon.label')}</FormLabel>
              <FormControl>
                <FileUploadInput
                  apiBaseUrl={API_BASE_URL}
                  value={form.favicon_url ?? ''}
                  onChange={(url) => update('favicon_url', url === '' ? null : url)}
                  accept="image/x-icon,image/png,image/svg+xml"
                  disabled={!canWrite}
                  testId="settings-general-favicon-url"
                  helperText={t('general.favicon.helper')}
                />
              </FormControl>
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('general.localeTime.title')}</CardTitle>
            <CardDescription>
              {t('general.localeTime.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormField>
              <FormLabel htmlFor="general-default-locale">
                {t('general.defaultLocale.label')}
              </FormLabel>
              <FormControl>
                <Select
                  value={form.default_locale}
                  onValueChange={(value) =>
                    update(
                      'default_locale',
                      value as GeneralSettingsValues['default_locale'],
                    )
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger
                    id="general-default-locale"
                    data-testid="settings-general-locale"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCALE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-default-tz">
                {t('general.defaultTimezone.label')}
              </FormLabel>
              <FormControl>
                <Select
                  value={form.default_timezone}
                  onValueChange={(value) => update('default_timezone', value)}
                  disabled={!canWrite}
                >
                  <SelectTrigger
                    id="general-default-tz"
                    data-testid="settings-general-timezone"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_TIMEZONES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                    {!COMMON_TIMEZONES.some(
                      (o) => o.value === form.default_timezone,
                    ) ? (
                      <SelectItem value={form.default_timezone}>
                        {form.default_timezone}
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-date-format">
                {t('general.dateFormat.label')}
              </FormLabel>
              <FormControl>
                <Select
                  value={form.default_date_format}
                  onValueChange={(value) =>
                    update('default_date_format', value)
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger
                    id="general-date-format"
                    data-testid="settings-general-date-format"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_FORMAT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-session-timeout">
                {t('general.sessionTimeout.label')}
              </FormLabel>
              <FormControl>
                <Input
                  id="general-session-timeout"
                  type="number"
                  min={5}
                  max={480}
                  value={form.session_timeout_minutes}
                  onChange={(e) =>
                    update(
                      'session_timeout_minutes',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-general-session-timeout"
                />
              </FormControl>
              <p className="text-xs text-pw-text-muted">{t('general.sessionTimeout.hint')}</p>
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('general.maintenance.title')}</CardTitle>
            <CardDescription>
              {t('general.maintenance.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div
              className={cn(
                'rounded-md border px-3 py-2 text-xs',
                form.maintenance.active
                  ? 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/40 text-orange-900'
                  : 'border-pw-border bg-pw-surface text-pw-text-muted',
              )}
              role="status"
            >
              {form.maintenance.active
                ? t('general.maintenance.bannerActive')
                : t('general.maintenance.bannerInactive')}
            </div>

            <FormField>
              <FormLabel htmlFor="general-maintenance-active">
                {t('general.maintenance.activeLabel')}
              </FormLabel>
              <FormControl>
                <Select
                  value={form.maintenance.active ? 'true' : 'false'}
                  onValueChange={(value) =>
                    updateMaintenance('active', value === 'true')
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger
                    id="general-maintenance-active"
                    data-testid="settings-general-maintenance-active"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">{t('general.maintenance.optionInactive')}</SelectItem>
                    <SelectItem value="true">{t('general.maintenance.optionActive')}</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-maintenance-message">
                {t('general.maintenance.messageLabel')}
              </FormLabel>
              <FormControl>
                <Input
                  id="general-maintenance-message"
                  value={form.maintenance.custom_message}
                  onChange={(e) =>
                    updateMaintenance('custom_message', e.target.value)
                  }
                  placeholder={t('general.maintenance.messagePlaceholder')}
                  disabled={!canWrite}
                  data-testid="settings-general-maintenance-message"
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-maintenance-duration">
                {t('general.maintenance.durationLabel')}
              </FormLabel>
              <FormControl>
                <Input
                  id="general-maintenance-duration"
                  type="number"
                  min={0}
                  max={168}
                  value={form.maintenance.duration_hours}
                  onChange={(e) =>
                    updateMaintenance(
                      'duration_hours',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-general-maintenance-duration"
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="general-maintenance-bypass">
                {t('general.maintenance.bypassLabel')}
              </FormLabel>
              <FormControl>
                <Input
                  id="general-maintenance-bypass"
                  value={form.maintenance.bypass_ips.join(', ')}
                  onChange={(e) => {
                    const ips = e.target.value
                      .split(',')
                      .map((ip) => ip.trim())
                      .filter((ip) => ip.length > 0);
                    updateMaintenance('bypass_ips', ips);
                  }}
                  placeholder={t('general.maintenance.bypassPlaceholder')}
                  disabled={!canWrite}
                  data-testid="settings-general-maintenance-bypass"
                />
              </FormControl>
            </FormField>
          </CardContent>
        </Card>

        <StickySaveBar
          dirty={dirty}
          saving={saving}
          canWrite={canWrite}
          onSave={() => {
            void save();
          }}
          onDiscard={() => {
            void reload();
          }}
        />
      </Form>

      {conflict ? (
        <ConflictDiffModal
          open={conflict.open}
          yourValues={conflict.yourValues as unknown as Record<string, unknown>}
          serverValues={conflict.serverValues as unknown as Record<string, unknown>}
          currentVersion={conflict.currentVersion}
          expectedVersion={conflict.expectedVersion}
          isSuperAdmin={isSuperAdmin}
          onReload={() => {
            dismissConflict();
            void reload();
          }}
          onForceOverwrite={() => {
            void forceOverwrite();
          }}
          onClose={dismissConflict}
        />
      ) : null}
    </div>
  );
}

export default GeneralTab;
